// Copyright (c) 2021 Deere & Company

import './index.scss';

import * as batchAdminRepository from './repositories';
import * as busyCounter from '../../shared/action-creator/busy-counter';
import { Link, withRouter } from 'react-router-dom';
import React, { Component } from 'react';
import { createErrorMessage, createSuccessMessage } from '../../shared/action-creator/alert-messages';
import BusyCounterLoadingIndicator from '../../shared/components/busyCounter/busy-counter-loading-indicator';
import LabeledReactSelect from '../../shared/components/options-drop-down/labeled-react-select';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { showLoadingIndicator } from '../../shared/state/busy-counter';

export class BatchAdmin extends Component {

    state = {
        batchType: null,
        unitName: null,
        batchTypeOptions: [],
        unitNameOptions: [],
        allBatchData: {},
        batchHistory: [],
        currentPage: 1,
        recordsPerPage: 20
    };

    componentDidMount () {

        this.loadBatchData();

    }

    loadBatchData = () => {

        this.props.increaseCounter();

        batchAdminRepository.getBatchUnits()
            .then( response => {

                console.log('Batch units response:', response);

                if ( response && typeof response === 'object' ) {

                    const batchData = response;
                    
                    // Extract batch types from keys
                    const batchTypes = Object.keys( batchData ).map( key => ( {
                        value: key,
                        label: key
                    } ) );

                    this.setState( {
                        batchTypeOptions: batchTypes,
                        allBatchData: batchData
                    } );

                }

            } )
            .catch( error => {

                this.props.createErrorMessage( 'Failed to load batch data' );
                console.error( 'Error loading batch data:', error );

            } )
            .finally( () => {

                this.props.decreaseCounter();

            } );

    };

    loadBatchHistory = () => {

        const { batchType, unitName } = this.state;

        this.props.increaseCounter();

        batchAdminRepository.getBatchHistory( 
            batchType ? batchType.value : '', 
            unitName ? unitName.value : '' 
        )
            .then( response => {

                console.log('Batch history response:', response);

                // Check if response is array directly or has data property
                const historyData = Array.isArray(response) ? response : (response && response.data);

                if ( historyData ) {

                    this.setState( {
                        batchHistory: historyData,
                        currentPage: 1
                    } );

                } else {
                    this.setState( {
                        batchHistory: [],
                        currentPage: 1
                    } );
                }

            } )
            .catch( error => {

                this.props.createErrorMessage( 'Failed to load batch history' );
                console.error( 'Error loading batch history:', error );

            } )
            .finally( () => {

                this.props.decreaseCounter();

            } );

    };

    handleBatchTypeChange = selectedOption => {

        // When batch type changes, update units for that batch type
        const units = this.state.allBatchData[selectedOption.value] || [];
        const unitOptions = [
            { value: '', label: 'All Units' },
            ...units.map( unit => ( {
                value: unit,
                label: unit
            } ) )
        ];

        this.setState( {
            batchType: selectedOption,
            unitName: { value: '', label: 'All Units' },
            unitNameOptions: unitOptions
        }, () => {
            // Load batch history after batch type is selected
            this.loadBatchHistory();
        } );

    };

    handleUnitNameChange = selectedOption => {

        this.setState( {
            unitName: selectedOption
        }, () => {
            // Load batch history after unit is selected
            this.loadBatchHistory();
        } );

    };

    handleRefresh = () => {

        this.loadBatchHistory();

    };

    handlePageChange = ( newPage ) => {

        this.setState( { currentPage: newPage } );

    };

    handleTriggerNow = () => {

        const { batchType, unitName } = this.state;

        if ( !batchType || !unitName || !unitName.value ) {

            this.props.createErrorMessage( 'Please select both Batch Type and Unit' );
            return;

        }

        this.props.increaseCounter();

        const batchData = {
            batchType: batchType.value,
            unitName: unitName.value,
            userFirstName: this.props.userFirstName || ''
        };

        batchAdminRepository.triggerBatch( batchData )
            .then( () => {

                this.props.createSuccessMessage( `Batch triggered successfully for ${batchType.label} - ${unitName.label}` );
                // Keep the selections - don't reset the form
                this.loadBatchHistory();

            } )
            .catch( error => {

                this.props.createErrorMessage( error.message || 'Failed to trigger batch' );

            } )
            .finally( () => {

                this.props.decreaseCounter();

            } );

    };

    render () {

        const { batchType, unitName, batchTypeOptions, unitNameOptions, batchHistory, currentPage, recordsPerPage } = this.state;

        // Calculate pagination
        const indexOfLastRecord = currentPage * recordsPerPage;
        const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
        const currentRecords = batchHistory.slice( indexOfFirstRecord, indexOfLastRecord );
        const totalPages = Math.ceil( batchHistory.length / recordsPerPage );
        
        // Custom styles for react-select to match button height
        const selectStyles = {
            control: ( base ) => ( {
                ...base,
                minHeight: '28px',
                height: '28px'
            } ),
            valueContainer: ( base ) => ( {
                ...base,
                height: '28px',
                padding: '0 8px'
            } ),
            input: ( base ) => ( {
                ...base,
                margin: '0',
                padding: '0'
            } ),
            indicatorsContainer: ( base ) => ( {
                ...base,
                height: '28px'
            } ),
            option: ( base ) => ( {
                ...base,
                fontSize: '10px',
                padding: '2px 8px',
                minHeight: 'unset'
            } ),
            singleValue: ( base ) => ( {
                ...base,
                fontSize: '11px'
            } ),
            placeholder: ( base ) => ( {
                ...base,
                fontSize: '11px'
            } )
        };

        return (
            <section className="main-content">
                <BusyCounterLoadingIndicator busyCounter={this.props.busyCounter} />
                <header>
                    <h2 className="page-header">Batch Admin Page</h2>
                    <Link className="family-availability-link" to="/batch-admin/reset-sna">Reset SNA</Link>
                </header>

                <main>
                    <section className="section-container">
                        <div className="batch-admin-card">
                            <div className="batch-admin-form">
                                <div className="form-row-horizontal">
                                    <LabeledReactSelect
                                        className="batch-admin-select"
                                        label="Batch Type:"
                                        isPlaceholderTranslated={false}
                                        isLabelTranslated={false}
                                        onChange={this.handleBatchTypeChange}
                                        optionKey="value"
                                        optionDisplayAttributes={['label']}
                                        options={batchTypeOptions}
                                        placeholder="Select Batch Type"
                                        selectedValue={batchType}
                                        styles={selectStyles}
                                        isSearchable={false}
                                    />
                                    
                                    <LabeledReactSelect
                                        className="batch-admin-select"
                                        label="Unit:"
                                        isPlaceholderTranslated={false}
                                        isLabelTranslated={false}
                                        onChange={this.handleUnitNameChange}
                                        optionKey="value"
                                        optionDisplayAttributes={['label']}
                                        options={unitNameOptions}
                                        placeholder="Select Unit"
                                        selectedValue={unitName}
                                        disabled={!batchType}
                                        styles={selectStyles}
                                        isSearchable={true}
                                    />

                                    <div className="button-group">
                                        <button
                                            className="secondary-button cancel-button"
                                            type="button"
                                            onClick={this.handleRefresh}
                                        >
                                            Refresh
                                        </button>
                                        <button
                                            className="primary-button trigger-button"
                                            type="button"
                                            onClick={this.handleTriggerNow}
                                        >
                                            Trigger Now
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {batchType && (
                            <div className="batch-history-section">
                                <h2 className="section-title">Batch Execution History</h2>
                                <div className="batch-history-table-container">
                                <table className="batch-history-table">
                                    <thead>
                                        <tr>
                                            <th>Unit</th>
                                            <th>Status</th>
                                            <th>Duration</th>
                                            <th>Start Time</th>
                                            <th>Task ID</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRecords.length > 0 ? (
                                            currentRecords.map( ( record, index ) => (
                                                <tr key={index}>
                                                    <td>{record.unit || 'N/A'}</td>
                                                    <td>
                                                        <span className={`status-badge status-${( record.status || '' ).toLowerCase()}`}>
                                                            {record.status || 'Unknown'}
                                                        </span>
                                                    </td>
                                                    <td>{record.duration || 'N/A'}</td>
                                                    <td>{record.start_time || 'N/A'}</td>
                                                    <td>{record.task_id || 'N/A'}</td>
                                                    <td>{record.remarks || 'N/A'}</td>
                                                </tr>
                                            ) )
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="no-data">
                                                    <span className={`status-badge status-failed`}>- - - No Batch History Available - - - </span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {batchHistory.length > recordsPerPage && (
                                <div className="pagination">
                                    <button 
                                        className="pagination-button" 
                                        onClick={() => this.handlePageChange( currentPage - 1 )}
                                        disabled={currentPage === 1}
                                    >
                                        ← Previous
                                    </button>
                                    <span className="pagination-info">
                                        Page {currentPage} of {totalPages} ({batchHistory.length} records)
                                    </span>
                                    <button 
                                        className="pagination-button" 
                                        onClick={() => this.handlePageChange( currentPage + 1 )}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    </section>
                </main>
            </section>
        );

    }

}

BatchAdmin.propTypes = {
    busyCounter: PropTypes.bool.isRequired,
    createErrorMessage: PropTypes.func,
    createSuccessMessage: PropTypes.func,
    decreaseCounter: PropTypes.func,
    increaseCounter: PropTypes.func,
    userFirstName: PropTypes.string
};

const mapStateToProps = state => ( {
    busyCounter: showLoadingIndicator( state.application.busyCounter ),
    accessToken: state.oktaState.oktaState.accessToken,
    userFirstName: state.oktaState.oktaState.userFirstName
} );

const mapDispatchToProps = dispatch => ( {
    createErrorMessage: message => dispatch( createErrorMessage( message ) ),
    createSuccessMessage: message => dispatch( createSuccessMessage( message ) ),
    decreaseCounter: () => dispatch( busyCounter.decreaseBusyCounter() ),
    increaseCounter: () => dispatch( busyCounter.increaseBusyCounter() )
} );

export default connect( mapStateToProps, mapDispatchToProps )( withRouter( BatchAdmin ) );
